import {
  type AllowedMaterialSpec,
  type BuyerMaterialSpecificationAdminListItem,
  isPortOfDischargeRequired,
  MaterialComponentCategory,
  type SimpleTradeCompanyListItem,
} from 'api';
import { convertFromMaterialComponentValue, MaterialSupportedComponentList } from 'features';
import {
  AllowedMaterialSpecAutocomplete,
  BuyerMaterialSpecificationsAutocomplete,
  DateField,
  IncotermSelectField,
  MaterialGradeAutocomplete,
  PortAutocomplete,
  PriceAsCurrencyOrIndex,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanyLeadAutocomplete,
  useFormContext,
  useWatch,
} from 'form';
import { useState } from 'react';

import { CompanySourceEnum, type CompanySourceType } from '../../enums';
import type { DemandSignalFormValues } from '../../schema/DemandSignalSchema/DemandSignalSchema';
import { CompanySourceToggleButton } from '../CompanySourceToggleButton/CompanySourceToggleButton';

export const DemandSignalFormSection = () => {
  const form = useFormContext<DemandSignalFormValues>();
  const { setValue } = form;
  const [incoterm, portOfDischargeId] = useWatch({
    name: ['incoterm', 'port_of_discharge_id'],
  });
  const [selectedCompanyType, setSelectedCompanyType] = useState<CompanySourceType>(CompanySourceEnum.GROUND_CONTROL);
  const [tradeCompany, setTradeCompany] = useState<SimpleTradeCompanyListItem | undefined>();
  const [buyerMaterialSpec, setBuyerMaterialSpec] = useState<BuyerMaterialSpecificationAdminListItem | undefined>();
  const [materialSpec, setMaterialSpec] = useState<AllowedMaterialSpec | undefined>();

  const onTradeCompanyChange = (tradeCompany: SimpleTradeCompanyListItem) => {
    if (tradeCompany.currency) {
      setValue('currency', tradeCompany.currency);
    }
    if (tradeCompany.preferred_port_of_discharge_id) {
      setValue('port_of_discharge_id', tradeCompany.preferred_port_of_discharge_id);
    }
    setTradeCompany(tradeCompany);
  };

  const onTradeCompanyTypeChange = (tradeCompanyType: CompanySourceType) => {
    setValue('trade_company_id', undefined);
    setValue('trade_company_lead_id', undefined);
    setValue('buyer_material_spec_id', undefined);
    setSelectedCompanyType(tradeCompanyType);
    setTradeCompany(undefined);
    setBuyerMaterialSpec(undefined);
  };

  const onBuyerMaterialSpecChange = (buyerMaterialSpec: BuyerMaterialSpecificationAdminListItem) => {
    setBuyerMaterialSpec(buyerMaterialSpec);
    setValue('material_grade_id', buyerMaterialSpec.material_grade.id);
    setValue(
      'material_component_values',
      convertFromMaterialComponentValue(buyerMaterialSpec.material_component_values)
    );
  };

  const onAllowedMaterialSpecChange = (materialSpec: AllowedMaterialSpec) => {
    setMaterialSpec(materialSpec);
  };

  return (
    <>
      <CompanySourceToggleButton
        onTradeCompanyTypeChange={onTradeCompanyTypeChange}
        tradeCompanyType={selectedCompanyType}
      />
      {selectedCompanyType === CompanySourceEnum.GROUND_CONTROL ? (
        <>
          <TradeCompanyAutocomplete
            name="trade_company_id"
            label="Select buyer from Ground Control"
            onlyBuyers={true}
            onSelectOption={(tradeCompany) => onTradeCompanyChange(tradeCompany)}
          />
          <BuyerMaterialSpecificationsAutocomplete
            name="buyer_material_spec_id"
            tradeCompanyId={tradeCompany?.id}
            onSelectOption={onBuyerMaterialSpecChange}
          />
        </>
      ) : (
        <TradeCompanyLeadAutocomplete name="trade_company_lead_id" label="Select buyer from Hubspot" />
      )}
      <AllowedMaterialSpecAutocomplete
        label="Material"
        name="material_spec_id"
        required
        onSelectOption={onAllowedMaterialSpecChange}
      />
      <MaterialSupportedComponentList
        name="material_component_values"
        materialId={buyerMaterialSpec?.material.id || materialSpec?.material_id}
        materialColourId={buyerMaterialSpec?.material_colour?.id || materialSpec?.colour_id || undefined}
        category={MaterialComponentCategory.MATERIAL_QUALITY}
        disabled={!!buyerMaterialSpec}
      />
      <MaterialGradeAutocomplete
        label="Material grade"
        name="material_grade_id"
        required
        disabled={!!buyerMaterialSpec}
      />
      <PriceAsCurrencyOrIndex />
      <IncotermSelectField fullWidth />
      {isPortOfDischargeRequired(incoterm) && (
        <PortAutocomplete name="port_of_discharge_id" label="Port of discharge" defaultValue={portOfDischargeId} />
      )}
      <TextField label="Tonnes" name="tonnes" type="number" required data-testid="tonnes" />
      <DateField label="Latest delivery date" name="latest_delivery_date" />
      <TextField multiline label="Standup notes" name="notes" aria-label="Notes" />
    </>
  );
};
